import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layout & Guards
import Layout from './components/layout/Layout';
import HorarioGuard from './components/HorarioGuard';

// Páginas
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DetalleRequerimiento from './pages/DetalleRequerimiento';
import NuevoRequerimiento from './pages/NuevoRequerimiento';
import BandejaEntrada from './pages/BandejaEntrada';
import BandejaEnviados from './pages/BandejaEnviados';
import BandejaSeguimiento from './pages/BandejaSeguimiento';
// Removed BandejaAnalistas import as it is now replaced by BandejaSeguimiento
import GerenciasPanel from './pages/GerenciasPanel';
import AdminDashboard from './pages/AdminDashboard';
import ReportesProveedores from './pages/ReportesProveedores';
import ErrorPage from './pages/ErrorPage';

// Todos los roles permitidos excepto presidencia (que tiene bypass)
const ROLES_VP = [
  'vicepresidencia', 'asistente_vicepresidencia',
];

const ROLES_ANALISTA = [
  'analista',
  'analista_administracion'
];

const ROLES_GESTION = [
  'vicepresidencia', 'asistente_vicepresidencia', 'administracion',
];

// Componente Wrapper para probar errores
const ErrorPageWrapper = () => {
  const { code } = useParams();
  return <ErrorPage errorCode={code} />;
};

// Componente de ruta protegida
const RutaProtegida = ({ children, rolesPermitidos }) => {
  const { isAuthenticated, cargando, usuario } = useAuth();

  if (cargando) {
    return <div className="loading">Cargando...</div>;
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Presidente (super admin) tiene acceso a todo
  if (usuario?.role === 'presidente' || usuario?.es_presidencia) {
    return children;
  }

  // Si hay roles específicos requeridos
  if (rolesPermitidos && !rolesPermitidos.includes(usuario?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated, cargando } = useAuth();

  if (cargando) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <HorarioGuard>
      <Routes>
        {/* Ruta pública */}
        <Route
          path="/login"
          element={isAuthenticated() ? <Navigate to="/" replace /> : <Login />}
        />

        {/* Dashboard principal */}
        <Route
          path="/"
          element={
            <RutaProtegida>
              <Layout>
                <Dashboard />
              </Layout>
            </RutaProtegida>
          }
        />

{/* Nuevo requerimiento (analistas + administracion + presidencia) */}
        <Route
          path="/requerimientos/nuevo"
          element={
            <RutaProtegida rolesPermitidos={[...ROLES_ANALISTA, 'administracion']}>
              <Layout>
                <NuevoRequerimiento />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Detalle de requerimiento */}
        <Route
          path="/requerimientos/:id"
          element={
            <RutaProtegida>
              <Layout>
                <DetalleRequerimiento />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Bandeja de entrada (VPs, asistentes VP, administración) */}
        <Route
          path="/bandeja-entrada"
          element={
            <RutaProtegida rolesPermitidos={[...ROLES_VP, 'administracion', 'analista_administracion']}>
              <Layout>
                <BandejaEntrada />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Bandeja analistas (nueva, con pestañas Nuevos/Pendientes/Pagados) */}
        <Route
          path="/mi-bandeja"
          element={
            <RutaProtegida rolesPermitidos={[...ROLES_ANALISTA, ...ROLES_VP]}>
              <Layout>
                <BandejaSeguimiento 
                  titulo="Mi Bandeja" 
                />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Enviados (analistas) */}
        <Route
          path="/enviados"
          element={
            <RutaProtegida rolesPermitidos={ROLES_ANALISTA}>
              <Layout>
                <BandejaEnviados />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Seguimiento (analistas + VPs) */}
        <Route
          path="/seguimiento"
          element={
            <RutaProtegida rolesPermitidos={[...ROLES_ANALISTA, ...ROLES_VP, 'administracion']}>
              <Layout>
                <BandejaSeguimiento />
              </Layout>
            </RutaProtegida>
          }
        />



        {/* Admin — Gestión de usuarios */}
        <Route
          path="/admin"
          element={
            <RutaProtegida rolesPermitidos={[...ROLES_VP, 'administracion']}>
              <Layout>
                <AdminDashboard />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Reportes de proveedores */}
        <Route
          path="/reportes-proveedores"
          element={
            <RutaProtegida rolesPermitidos={['administracion', 'analista_administracion']}>
              <Layout>
                <ReportesProveedores />
              </Layout>
            </RutaProtegida>
          }
        />

        {/* Manual Error Testing Routes */}
        <Route path="/error/:code" element={<ErrorPageWrapper />} />

        {/* Ruta no encontrada */}
        <Route path="*" element={<ErrorPage errorCode={404} />} />
      </Routes>
    </HorarioGuard>
  );
}

export default App;
